import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import request, { post } from "../../internal/request";
import { Diff } from "../cache-view/cache-view";

import "./publish-brand-item.css"
import { FormCheck } from "react-bootstrap";
import { API_ADDR } from "../../App";

const StatusPending = "-"
const StatusDiffInProgress = "Diff in progress"
const StatusPublishInProgress = "Publishing..."
const StatusPublished = "Published"
const StatusReadyToPublish = "Ready to publish"
const StatusNotAvailable = "Not available to publish"
const StatusError = "Error"
const StatusUpToDate = "Already up to date"
const StatusNotPublish = "Will not publish"

interface Props {
  hotelReferenceId: string
  environment: string
  hotelProcessed: (hotelReferenceId: string, isDiff: boolean, willPublish: boolean) => void
}

// Define the ref type
export type PublishBrandItemRef = {
  reset: () => void
  diff: () => void
  publish: () => void
};

const PublishBrandItem = forwardRef<PublishBrandItemRef, Props>((props, ref) => {
  const [status, setStatus] = useState(StatusPending)
  const [action, setAction] = useState("")
  const [publishChecked, setPublishChecked] = useState(false)
  const [publishEnabled, setPublishEnabled] = useState(false)

  useImperativeHandle(ref, () => ({
    reset: () => {
      setStatus(StatusPending)
    },
    diff: () => {
      setAction("diff")
    },
    publish: () => {
      setAction("publish")
    }
  }))

  useEffect(() => {
    if (action === "diff") {
      setAction("")
      setStatus(StatusDiffInProgress)
      request<Diff[]>(`${API_ADDR()}/api/diff?hotelreferenceid=${props.hotelReferenceId}&environment=${props.environment}`)
        .then((diffs) => {
          const diffStatus = diffs[0]?.status ?? "ERROR"
          const newStatus = diffStatus === "ERROR"
            ? StatusError
            : diffStatus === "NOT_AVAILABLE"
              ? StatusNotAvailable
              : diffStatus === "DIFFERENT" || diffStatus === "NOT_CACHED"
                ? StatusReadyToPublish
                : StatusUpToDate
          setStatus(newStatus)
          props.hotelProcessed(props.hotelReferenceId, true, newStatus === StatusReadyToPublish)
        })
        .catch((error) => {
          setStatus(StatusError)
          props.hotelProcessed(props.hotelReferenceId, true, false)
        })
    }
    if (action === "publish") {
      // Publish me!
      setAction("")
      if (status === StatusReadyToPublish) {
        setStatus(StatusPublishInProgress)

        post(`${API_ADDR()}/api/publish?hotelreferenceid=${props.hotelReferenceId}&environment=${props.environment}`)
          .then((res) => {
            if (res.status === 204) {
              // All good: Publish succeeded. Refresh somehow.
              setStatus(StatusPublished)
              props.hotelProcessed(props.hotelReferenceId, false, true)
            } else {
              setStatus(StatusError)
              props.hotelProcessed(props.hotelReferenceId, false, false)
            }
          })
          .catch((error) => {
            setStatus(StatusError)
            props.hotelProcessed(props.hotelReferenceId, false, false)
          })

      }
    }

  }, [action, status, props])

  useEffect(() => {
    if (status === StatusReadyToPublish || status === StatusNotPublish) {
      setPublishEnabled(true)
      setPublishChecked(status === StatusReadyToPublish)
    } else {
      setPublishEnabled(false)
      setPublishChecked(false)
    }

  }, [status])

  function classFromStatus(input: string): string {
    switch (input) {
      case StatusReadyToPublish:
        return "publish"
      case StatusUpToDate:
      case StatusPublished:
      case StatusNotPublish:
        return "cacheok"
      case StatusError:
        return "error"
    }
    return ""
  }

  function toggleWillPublish() {
    // If status is StatusReadyToPublish, change to StatusNotPublish, or vice versa
    if (status === StatusReadyToPublish) {
      setStatus(StatusNotPublish)
      props.hotelProcessed(props.hotelReferenceId, true, false)
    }
    else if (status === StatusNotPublish) {
      setStatus(StatusReadyToPublish)
      props.hotelProcessed(props.hotelReferenceId, true, true)
    }
  }

  return <>
    <tr>
      <td>{props.hotelReferenceId}</td>
      <td className={classFromStatus(status)} style={{ textAlign: "center" }}>{status}</td>
      <td style={{ textAlign: "center" }}>
        <FormCheck
          onChange={() => toggleWillPublish()}
          checked={publishChecked}
          disabled={!publishEnabled}
        />
      </td>
    </tr>
  </>
})



export default PublishBrandItem